import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, ShieldOff } from 'lucide-react';

import { Button } from '@/components/Common/button';
import { useLayoutStore } from '@/stores/layoutStore';

export interface AccessDeniedProps {
  /** Optional page-specific explanation; falls back to a generic message. */
  description?: string;
  title?: string;
}

/**
 * Full-screen no-access page for routes the user opened without permission
 * (e.g. org admin hitting /admin/profiles after the Administration menu was hidden).
 * Hides the app header and sidebar while mounted, so the page doesn't look reachable.
 */
const AccessDenied: React.FC<AccessDeniedProps> = ({ description, title }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const setChromeHidden = useLayoutStore((s) => s.setChromeHidden);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Layout effect: drop the header and sidebar before the first paint, not after a flash.
  useLayoutEffect(() => {
    setChromeHidden(true);
    return () => setChromeHidden(false);
  }, [setChromeHidden]);

  // Screen readers land on the explanation instead of the top of a page that no longer exists.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // Opened from a pasted URL there is no in-app history: "back" would leave the app.
  const goBack = () => {
    const historyIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (historyIndex > 0) navigate(-1);
    else navigate('/', { replace: true });
  };

  return (
    <main className="relative flex w-full flex-1 items-center justify-center overflow-y-auto bg-background px-4 py-16">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(hsl(var(--border))_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl"
      />

      <div className="relative flex w-full max-w-md flex-col items-center text-center">
        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-card shadow-sm">
          <ShieldOff className="h-8 w-8 text-primary" aria-hidden />
        </div>

        <span className="mb-4 inline-flex items-center rounded-full border border-border bg-muted/60 px-3 py-1 font-mono text-xs font-medium text-muted-foreground">
          {t('accessDenied.code', 'Error 403')}
        </span>

        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mb-3 text-3xl font-semibold tracking-tight text-foreground outline-none sm:text-4xl"
        >
          {title ?? t('accessDenied.title', 'Access denied')}
        </h1>

        <p className="mb-2 text-base text-muted-foreground">
          {description
            ?? t('accessDenied.description', "Sorry, you don't have permission to access this page.")}
        </p>
        <p className="mb-8 text-sm text-muted-foreground">
          {t('accessDenied.hint', 'If you think you should have access, contact your administrator.')}
        </p>

        <div className="flex w-full flex-col-reverse gap-3 sm:w-auto sm:flex-row">
          <Button type="button" variant="outline" size="lg" onClick={goBack}>
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
            {t('accessDenied.goBack', 'Go back')}
          </Button>
          <Button type="button" size="lg" onClick={() => navigate('/', { replace: true })}>
            <Home className="mr-2 h-4 w-4" aria-hidden />
            {t('accessDenied.goHome', 'Home')}
          </Button>
        </div>

        {/* <p className="mt-10 max-w-full truncate font-mono text-xs text-muted-foreground/70" title={pathname}>
          {pathname}
        </p> */}
      </div>
    </main>
  );
};

export default AccessDenied;
